import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export default function useScreenWakeLock(enabled) {
  const wakeLockRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [lastError, setLastError] = useState(null);

  const isSupported = useMemo(() => {
    return (
      typeof navigator !== 'undefined' &&
      !!navigator.wakeLock &&
      typeof navigator.wakeLock.request === 'function'
    );
  }, []);

  const release = useCallback(async () => {
    const sentinel = wakeLockRef.current;
    wakeLockRef.current = null;
    setIsActive(false);

    if (!sentinel) {
      return;
    }

    try {
      await sentinel.release();
    } catch (_) {
      // Ignore release errors (e.g., already released by the UA)
    }
  }, []);

  const request = useCallback(async () => {
    if (!isSupported) {
      return false;
    }

    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return false;
    }

    const existing = wakeLockRef.current;
    if (existing && !existing.released) {
      setIsActive(true);
      return true;
    }

    try {
      const sentinel = await navigator.wakeLock.request('screen');
      wakeLockRef.current = sentinel;
      setIsActive(true);
      setLastError(null);

      if (sentinel && typeof sentinel.addEventListener === 'function') {
        sentinel.addEventListener('release', () => {
          if (wakeLockRef.current === sentinel) {
            wakeLockRef.current = null;
          }
          setIsActive(false);
        });
      }

      return true;
    } catch (error) {
      setIsActive(false);
      setLastError({
        name: error?.name || 'WakeLockError',
        message: error?.message || 'Failed to acquire wake lock',
      });
      return false;
    }
  }, [isSupported]);

  useEffect(() => {
    if (!enabled) {
      release();
      return undefined;
    }

    request();
    return () => {
      release();
    };
  }, [enabled, request, release]);

  useEffect(() => {
    if (!enabled || !isSupported || typeof document === 'undefined') {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        request();
      } else {
        release();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, isSupported, request, release]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handlePageHide = () => {
      release();
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [release]);

  return {
    isSupported,
    isActive,
    lastError,
    request,
    release,
  };
}

